import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Grid, Card, CardContent, Typography, TextField, FormControl,
    InputLabel, Select, MenuItem, Snackbar, Alert
} from "@mui/material";

import MantenimientoForm from "../../components/common/MantenimientoForm";
import { useMantenimiento } from "../../hooks/useMantenimiento";
import * as grupoCargoService from "../../services/grupoCargoService";

/**
 * Pantalla de mantenimiento para Grupo de Cargo.
 * Sigue la plantilla estándar del sistema:
 * - Botón Guardar centrado arriba y abajo (vía MantenimientoForm).
 * - Enlace Volver a la navegabilidad /grupocargo.
 * - Tarjeta con datos básicos y validaciones.
 * - Tarjeta con información de auditoría visible en edición (id > 0).
 */
const Edit = () => {
    const params = useParams();
    const navigate = useNavigate();
    const id = parseInt(params.id || 0, 10);
    const isEdicion = id > 0;

    const { isReadOnly, setIsReadOnly } = useMantenimiento("trabajador");

    const [codigo, setCodigo] = useState("");
    const [descripcion, setDescripcion] = useState("");
    const [idEstado, setIdEstado] = useState(2);

    const [audit, setAudit] = useState({
        fechaRegistro: null,
        usuarioRegistro: "",
        fechaModificacion: null,
        usuarioModificacion: "",
    });

    const [cargando, setCargando] = useState(false);
    const [openMessage, setOpenMessage] = useState(false);
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    const mostrarMensaje = (severity, message) => {
        setPropsMessage({ severity, message });
        setOpenMessage(true);
    };

    const fetchEntity = useCallback(async () => {
        if (!isEdicion) {
            setCodigo("");
            setDescripcion("");
            setIdEstado(2);
            setAudit({
                fechaRegistro: null,
                usuarioRegistro: "",
                fechaModificacion: null,
                usuarioModificacion: "",
            });
            return;
        }

        setCargando(true);
        try {
            const data = await grupoCargoService.getGrupoCargoViewById(id);
            if (data) {
                setCodigo(data.codigo || "");
                setDescripcion(data.descripcion || "");
                setIdEstado(data.idEstado ?? 2);
                setAudit({
                    fechaRegistro: data.fechaRegistro || null,
                    usuarioRegistro: data.usuarioRegistro || "",
                    fechaModificacion: data.fechaModificacion || null,
                    usuarioModificacion: data.usuarioModificacion || "",
                });
            }
        } catch (err) {
            console.error("Error al cargar grupo de cargo:", err);
            mostrarMensaje("error", "Error al cargar la información del grupo de cargo");
        }
        setCargando(false);
    }, [id, isEdicion]);

    useEffect(() => {
        fetchEntity();
    }, [fetchEntity]);

    const postEntity = async () => {
        if (!codigo || codigo.trim() === "") {
            mostrarMensaje("error", "El código del grupo de cargo es obligatorio");
            return;
        }
        if (!descripcion || descripcion.trim() === "") {
            mostrarMensaje("error", "La descripción del grupo de cargo es obligatoria");
            return;
        }

        try {
            const resultId = await grupoCargoService.postGrupoCargo({
                id: id,
                codigo: codigo.trim().toUpperCase(),
                descripcion: descripcion.trim(),
                idEstado: idEstado,
                fechaRegistro: audit.fechaRegistro,
                usuarioRegistro: audit.usuarioRegistro,
            });

            const savedId = typeof resultId === "number" ? resultId : (resultId?.data || id);
            if (savedId > 0) {
                mostrarMensaje("success", "Se guardaron los datos con éxito");
                setIsReadOnly(true);

                if (!isEdicion && savedId > 0) {
                    navigate("/grupocargo/edit/" + savedId);
                } else {
                    fetchEntity();
                }
            } else {
                mostrarMensaje("error", "No se pudo guardar el grupo de cargo");
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err.message || "Error al guardar";
            mostrarMensaje("error", msg);
        }
    };

    return (
        <div>
            <MantenimientoForm
                title={(isEdicion ? "Editar" : "Nuevo") + " Grupo de Cargo"}
                backTo="/grupocargo"
                onSave={postEntity}
                disabled={isReadOnly || cargando}
            >
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {/* Tarjeta de Datos Básicos */}
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
                                    Datos Básicos
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            required
                                            fullWidth
                                            size="small"
                                            label="Código"
                                            value={codigo}
                                            onChange={(e) => setCodigo(e.target.value)}
                                            disabled={isReadOnly}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ maxLength: 20 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            required
                                            fullWidth
                                            size="small"
                                            label="Descripción"
                                            value={descripcion}
                                            onChange={(e) => setDescripcion(e.target.value)}
                                            disabled={isReadOnly}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ maxLength: 100 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel shrink>Estado</InputLabel>
                                            <Select
                                                label="Estado"
                                                value={idEstado}
                                                onChange={(e) => setIdEstado(Number(e.target.value))}
                                                disabled={isReadOnly}
                                            >
                                                <MenuItem value={2}>Activo</MenuItem>
                                                <MenuItem value={1}>Anulado</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </MantenimientoForm>

            <Snackbar
                open={openMessage}
                autoHideDuration={5000}
                onClose={() => setOpenMessage(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={() => setOpenMessage(false)} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default Edit;

